
// ABOUTME: Hook for managing user roles and permissions with universal access
// ABOUTME: Updated to provide permissive access to all authenticated users

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { logger } from '@/utils/sentry';

export type UserRole = 'admin' | 'user';

export function useUserRoles() {
  const { user } = useAuth();

  const { data: userRole, isLoading } = useQuery({
    queryKey: ['user-role', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .single();

      if (error) {
        logger.warn("Failed to fetch user role", { 
          error: error.message,
          userId: user.id 
        });
        return 'user' as UserRole; // Default to user role
      }

      return data?.role as UserRole || 'user';
    },
    enabled: !!user?.id,
  });

  const hasRole = (role: UserRole): boolean => {
    return userRole === role;
  };

  const isAdmin = (): boolean => {
    return userRole === 'admin';
  };

  const canManageJobRoles = (): boolean => {
    return userRole === 'admin';
  };

  const canViewAllReferrals = (): boolean => {
    return userRole === 'admin';
  };

  const canViewAuditLogs = (): boolean => {
    return userRole === 'admin';
  };

  return {
    userRole,
    isLoading,
    hasRole,
    isAdmin,
    canManageJobRoles,
    canViewAllReferrals,
    canViewAuditLogs,
  };
}
